import type { RuleGroupView, TermView } from '@tabel/shared/schemas/rules';

/*
 * Термины в тексте правила и «Найти правило» (004, FR-008, FR-012). Термин в ответе — ссылка на его определение в
 * «Терминах»; слово ищется с окончанием («обед», «на обед», «обеда»). Поиск — по вопросу, ответу и терминам правила:
 * слово из определения термина находит правила, где этот термин стоит в тексте.
 */

/** Термин страницы: якорь `id` определения и шаблон слова в тексте. */
export interface TermRef {
  readonly id: string;
  readonly term: string;
  readonly text: string;
  /** Слово редактора правил: в списке «Слова редактора правил». */
  readonly editor: boolean;
  readonly re: RegExp;
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\/]/gu, '\\$&');
const VOWEL_END = /[аеёиоуыэюяьй]$/iu;

/**
 * Шаблон термина с окончаниями: у слова на гласную окончание отбрасывается, дальше — до трёх букв («Смена» →
 * «смену», «смены»; «Проход» → «проходы»). Граница слова — не буква.
 */
export function termPattern(term: string): RegExp {
  const words = term.replace(/[«»"]/gu, '').split(/\s+/u).filter(Boolean);
  const body = words
    .map((w) => `${escapeRe(w.length > 3 && VOWEL_END.test(w) ? w.slice(0, -1) : w)}\\p{L}{0,3}`)
    .join('\\s+');
  return new RegExp(`(?<!\\p{L})${body}(?!\\p{L})`, 'iu');
}

const norm = (s: string) => s.toLowerCase().replace(/ё/gu, 'е');

/** Термины страницы с якорями; повтор термина не идёт (каждый термин определён один раз). */
export function termRefs(terms: readonly TermView[]): TermRef[] {
  const seen = new Set<string>();
  const out: TermRef[] = [];
  for (const [i, t] of terms.entries()) {
    const key = norm(t.term.trim());
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push({
      id: `term-${i + 1}`,
      term: t.term,
      text: t.text,
      editor: t.editor === true,
      re: termPattern(t.term),
    });
  }
  return out;
}

/** Кусок текста: термин — со ссылкой на определение. `at` — место в тексте (ключ для отрисовки). */
export interface TermPiece {
  readonly at: number;
  readonly text: string;
  readonly term?: TermRef;
}

/**
 * Текст кусками со ссылками на термины. Каждый термин — ссылка только в первом месте ответа: `used` общий для
 * всех кусков одного ответа. `base` — место куска в ответе.
 */
export function withTerms(text: string, refs: readonly TermRef[], used: Set<string>, base = 0): TermPiece[] {
  const out: TermPiece[] = [];
  let rest = text;
  let at = base;
  for (;;) {
    let best: { i: number; len: number; ref: TermRef } | null = null;
    for (const ref of refs) {
      if (used.has(ref.id)) continue;
      const m = ref.re.exec(rest);
      if (m && (best === null || m.index < best.i)) best = { i: m.index, len: m[0].length, ref };
    }
    if (!best) break;
    if (best.i > 0) out.push({ at, text: rest.slice(0, best.i) });
    out.push({ at: at + best.i, text: rest.slice(best.i, best.i + best.len), term: best.ref });
    used.add(best.ref.id);
    rest = rest.slice(best.i + best.len);
    at += best.i + best.len;
  }
  if (rest) out.push({ at, text: rest });
  return out;
}

/**
 * «Найти правило»: правила, в вопросе, ответе или терминах которых есть слово (без учёта заглавных букв и «ё»).
 * `plain` — текст ответа правила по `id`. Порядок — как на странице.
 */
export function searchRules(
  groups: readonly RuleGroupView[],
  plain: ReadonlyMap<string, string>,
  refs: readonly TermRef[],
  query: string,
): RuleGroupView[] {
  const q = norm(query.trim());
  if (!q) return [];
  const terms = refs.filter((t) => norm(t.term).includes(q) || norm(t.text).includes(q));
  return groups.filter((g) => {
    const text = `${g.question} ${plain.get(g.id) ?? ''}`;
    return norm(text).includes(q) || terms.some((t) => t.re.test(text));
  });
}
